'use client';

import { HTMLProps, type MouseEvent } from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useToggle } from 'react-use';

import { Button } from '@/components/common/Button';
import { CheckIcon } from '@/components/icons';
import { useJobsAnalytics, type JobSurface } from '@/analytics/jobs.analytics';
import { useRoleApplication } from '@/services/jobs/hooks/useJobApplications';
import { useRoleSavedJob } from '@/services/jobs/hooks/savedJobs/useRoleSavedJob';

import type { IJobRole, IJobTeam } from '@/types/jobs.types';
import type { JobDetailTarget } from '@/components/page/jobs/hooks/useJobApplyFlow';
import { canSeeOriginalPosting } from '@/services/jobs/job-board-viewer';
import { jobOpeningPath } from '@/services/jobs/job-detail-link';
import { formatRelativeDays, getJobDate, isNew, seniorityDisplayLabel } from '@/utils/jobs.utils';

import { jobApplyQueryParams } from './constants';

import { ReferMenu } from './components/ReferMenu';
import { RoleOwnerMenu } from './components/RoleOwnerMenu';
import { SaveRoleButton } from './components/SaveRoleButton';
import { ArrowIcon, ClockIcon } from './components/Icons';
import { ReferModal } from '@/prototypes/entries/job-board/components/ReferModal/ReferModal';

import s from './ReferRoleRow.module.scss';
// Button's own stylesheet, so the "Applied" state is the real DS button shape.
import btn from '@/components/common/Button/Button.module.scss';
import ap from './ReferRoleRowApply.module.scss';
import { IUserInfo } from '@/types/shared.types';
import { useLoginRedirect } from '@/components/core/login/utils';

/**
 * In-app Apply, switched on by prop PRESENCE — the row imports no feature flag.
 * A host (`JobsContent`, `TeamOpenRolesSection`) passes this only when
 * `SHOW_JOB_BOARD_APPLY` is on and the viewer may apply, which makes flag-off
 * byte-identity structural rather than test-enforced. Both hosts build it from
 * the same `useJobApplySurface`.
 */
export interface RowApplyProps {
  onApply: (target: JobDetailTarget) => void;
  /** Scopes the applied-map subscription; undefined while logged out. */
  memberUid: string | undefined;
  /**
   * Read the job in the app first.
   *
   * Presence swaps the row's one button from **Apply** to **View job** and
   * moves Apply to the bottom of the description it applies to — a row carries
   * a title, a seniority and a location, which is not enough to decide with, so
   * pressing Apply from it was pressing send on a job you had not read.
   *
   * Optional for the same reason `apply` is: the host decides. Both the board
   * and the team profile pass it with every apply slot, now that reading is
   * step 1 of the flow. A surface that omits it keeps the direct Apply.
   */
  onViewJob?: (target: { role: IJobRole; teamId: string; teamName: string; team: IJobTeam }) => void;
}

/**
 * Bookmarking, switched on by prop PRESENCE as `apply` is. Here it gates a
 * surface rather than a flag: the board passes it, the team profile does not,
 * because only the board has a Saved tab to find the role again in.
 */
export interface RowSaveProps {
  /** Scopes the saved-map subscription; undefined while logged out, which is
   *  also what makes the press open the sign-in door instead of writing. */
  memberUid: string | undefined;
  /** Inside the Saved tab the clock reports the save instead of the posting's
   *  age — there, "9d ago" reads as when you saved it. */
  savedScope: boolean;
}

interface ReferRoleRowProps {
  /**
   * The viewer is a lead of this team (or a Directory admin) — so this is their
   * own listing, and every press collapses into one ⋯ at the end of the row.
   *
   * Off everywhere by default, including the board: an applicant needs Refer,
   * Share and the way in at a glance, and the row is unchanged for them. Only
   * the team's own profile passes it, where a lead reads past the same three
   * controls on every row of their own page.
   */
  ownsListing?: boolean;
  /**
   * Drop the **View job** button from the action slot.
   *
   * The title is the same door — with the in-app description on, both open the
   * drawer, which is why the row's own note calls them "one door with two
   * handles". On a team's profile the handle is redundant: every row belongs to
   * the team whose page you are already reading, so a button per row repeats an
   * offer the titles already make.
   *
   * Only the button. Apply and Applied are different presses and are untouched,
   * and so is every surface that does not pass this — the board's rows, where
   * the button is the one action a scanner has.
   */
  hideViewJob?: boolean;
  role: IJobRole;
  teamId: string;
  teamName: string;
  currentUser: IUserInfo | null;
  /** Which surface this row is rendered on — drives analytics and the outbound utm_medium. */
  source: JobSurface;
  /**
   * The full team record, for surfaces that can open the in-app description —
   * its masthead needs the logo and focus areas that `teamId`/`teamName` cannot
   * carry. Both surfaces pass it.
   *
   * View job is offered only when this AND `apply.onViewJob` are present, so a
   * drawer can never be opened without the masthead it renders.
   */
  team?: IJobTeam;
  onClick?: () => void;
  apply?: RowApplyProps;
  save?: RowSaveProps;
}

/** Left-click stays in-place (drawer / feed new-tab). Modified clicks follow the href. */
function interceptUnmodifiedClick(event: MouseEvent<HTMLAnchorElement>, onOpen: () => void) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  onOpen();
}

/**
 * COPY-SIMPLIFY of production `RoleRow` with a per-job "Refer" control added.
 * The whole row is no longer a single <a> (can't nest a button in an anchor):
 * the title + arrow are the apply link, and the ReferMenu sits alongside the meta.
 *
 * With `apply` present (the in-app apply flow), the trailing slot becomes a real
 * Apply button — rendered regardless of `applyUrl`, which makes link-less roles
 * appliable for the first time — and Refer drops to the quiet text button (Apply
 * is what the row is for; Refer is the sideline). When the row opens the job
 * drawer, the external arrow is gone: the drawer is the way to read the posting.
 * A row already applied to reports "Applied" in the same geometry instead of
 * offering again.
 */
export function ReferRoleRow(props: ReferRoleRowProps) {
  const { role, teamId, teamName, currentUser, source, onClick, apply, team, save, ownsListing, hideViewJob } = props;

  const goToLogin = useLoginRedirect();

  /* The way out to the company's own posting — withheld from the two people who
     came here to apply through this board. See `canSeeOriginalPosting`. */
  const showPosting = canSeeOriginalPosting({ isLoggedIn: Boolean(currentUser), userInfo: currentUser });
  const analytics = useJobsAnalytics();
  const [referOpen, toggleReferOpen] = useToggle(false);

  /* Both, or neither — the same rule `viewJob` follows, and for the same
     reason: the flow opens on the reading step, which needs the team record to
     draw its masthead. A row without one cannot start a flow. */
  const inAppApply = Boolean(apply && team);
  // Per-row subscription to the shared applied map: one application re-renders
  // exactly this row, never the list. Inert (enabled: false) without apply props.
  const application = useRoleApplication(role.uid, { memberUid: apply?.memberUid, enabled: Boolean(apply?.memberUid) });
  const applied = Boolean(application);
  // Same per-row subscription as the application above, on the saved map.
  const savedJob = useRoleSavedJob(role.uid, { memberUid: save?.memberUid, enabled: Boolean(save?.memberUid) });
  const onViewJob = apply?.onViewJob;
  /* Both, or neither — see the `team` prop. */
  const viewJob = onViewJob && team ? () => onViewJob({ role, teamId, teamName, team }) : null;

  const { location, seniority, roleTitle, applyUrl, roleCategory } = role;

  const date = getJobDate(role);
  const relative = formatRelativeDays(date);
  const showNew = isNew(date);
  const savedRelative = save?.savedScope && savedJob ? `Saved ${formatRelativeDays(savedJob.savedAt)}` : null;
  /* Applied outranks saved: a role you both kept and sent is, to you, sent. */
  const clockLabel = application ? `Applied ${formatRelativeDays(application.appliedAt)}` : (savedRelative ?? relative);
  const locationDisplay = isEmpty(location) ? null : location.join(', ');

  const metaParts = [seniority ? seniorityDisplayLabel(seniority) : null, roleCategory, locationDisplay].filter(
    Boolean,
  );

  // Not every posting has a destination. Without one there is nothing to open, so the
  // row drops the title link and the arrow rather than rendering an <a> with no href:
  // `.titleLink` and `.applyArrow` both carry hover states and a pointer cursor, which
  // would offer a click that silently does nothing. (In-app Apply is unaffected — it
  // never leaves the page, so it renders with or without a URL.)
  const hasApplyUrl = Boolean(applyUrl);

  const linkProps: HTMLProps<HTMLAnchorElement> = hasApplyUrl
    ? { href: `${applyUrl}?${jobApplyQueryParams(source)}`, target: '_blank', rel: 'noopener noreferrer', onClick }
    : {};

  const referBase = {
    job_id: role.uid,
    team_id: teamId,
    team_name: teamName,
    role_title: roleTitle,
    role_category: roleCategory,
    seniority,
    source,
    uses_team_refer_email: Boolean(team?.jobReferEmail?.trim()),
  };

  function onRefer() {
    const authRequired = !currentUser;
    analytics.onJobReferClicked({ ...referBase, auth_required: authRequired });

    if (currentUser) {
      toggleReferOpen();
    } else {
      goToLogin();
    }
  }

  return (
    <div className={`${s.root} ${s.row}`}>
      <div className={s.body}>
        <div className={s.titleRow}>
          {/* Permalink so crawlers (and open-in-new-tab) reach `/jobs/openings/<uid>`.
              An unmodified click still opens the in-app reading of the job, so the
              title and View job stay one door. Surfaces without that reading keep
              the outbound apply link. */}
          {viewJob ? (
            <a
              className={`${s.title} ${s.titleLink}`}
              href={jobOpeningPath(role.uid)}
              onClick={(event) => interceptUnmodifiedClick(event, viewJob)}
            >
              {roleTitle}
            </a>
          ) : hasApplyUrl ? (
            <a className={`${s.title} ${s.titleLink}`} {...linkProps}>
              {roleTitle}
            </a>
          ) : (
            <span className={s.title}>{roleTitle}</span>
          )}
          {/* Mobile-only: "New" aligned to the top-right, in line with the role name. */}
          {showNew && <span className={`${s.newBadge} ${s.newBadgeMobile}`}>● New</span>}
        </div>
        {!isEmpty(metaParts) && <div className={s.meta}>{metaParts.join(' · ')}</div>}
      </div>

      <div className={`${s.right} ${s.actions}`}>
        {showNew && <span className={`${s.newBadge} ${s.newBadgeDesktop}`}>● New</span>}
        {/* Once applied, the clock reports the application rather than the
            posting's age — which is what makes a second "Applied" chip in the
            action slot unnecessary below. */}
        {clockLabel && (
          <span className={clsx(s.relative, inAppApply && ap.relativeTone)}>
            <ClockIcon />
            {clockLabel}
          </span>
        )}

        <div className={s.actionButtons}>
          {ownsListing ? (
            /* One control instead of three. The same presses are all in here —
               see `RoleOwnerMenu`, which also explains why the design's
               `Mark inactive` and `Delete` are not. */
            <RoleOwnerMenu
              role={role}
              teamId={teamId}
              teamName={teamName}
              source={source}
              onRefer={onRefer}
              onViewJob={viewJob ?? undefined}
              postingHref={showPosting && hasApplyUrl ? (linkProps.href as string) : undefined}
            />
          ) : inAppApply ? (
            /* Quiet text button: with a filled Apply in the row, the two are not
               peers — Apply is what the row is for, Refer is the sideline. */
            <Button size="s" style="link" variant="secondary" className={ap.referTone} onClick={onRefer}>
              Refer
            </Button>
          ) : (
            <Button size="s" style="border" variant="neutral" className={s.referButton} onClick={onRefer}>
              Refer
            </Button>
          )}

          {!ownsListing && <ReferMenu role={role} teamId={teamId} teamName={teamName} source={source} />}

          {save && (
            <SaveRoleButton
              role={role}
              teamId={teamId}
              source={source}
              memberUid={save.memberUid}
              saved={Boolean(savedJob)}
            />
          )}

          {!ownsListing &&
            showPosting &&
            hasApplyUrl &&
            !viewJob &&
            (inAppApply ? (
              <a
                className={`${s.applyArrow} ${ap.arrowTone}`}
                aria-label={`Open the ${roleTitle} posting`}
                {...linkProps}
              >
                <ArrowIcon />
              </a>
            ) : (
              <a className={s.applyArrow} aria-label={`Apply to ${roleTitle}`} {...linkProps}>
                <ArrowIcon />
              </a>
            ))}

          {/* The board's one action, once the description moved in-app. Apply is
              no longer here: the row's job is now the reading step, and Apply
              sits at the bottom of what it applies to.

              One button in both states, because the applied fact is already in
              this row — the clock to the left reads "Applied 3d ago" instead of
              the posting's age. A second report of the same fact, in the slot
              that used to hold the offer, would only be filling the space the
              offer left. And having applied is no reason to stop being able to
              reread the job. The drawer's own footer carries the Applied
              control, where the offer it replaces is. */}
          {/* Absent for the listing's own team: View job is the first item in
              their ⋯, and a lead is not applying to their own role. */}
          {!ownsListing &&
            (viewJob
              ? !hideViewJob && (
                  <Button size="s" style="fill" variant="primary" className={ap.applyButton} onClick={viewJob}>
                    View job
                  </Button>
                )
              : inAppApply &&
                (applied ? (
                  /* Same slot, same geometry: a row you've applied to must not
               resize the list around it. `disabled` is the honest semantics —
               there is nothing left to press. */
                  <button
                    type="button"
                    disabled
                    className={clsx(btn.root, btn.small, btn.border, btn.neutral, ap.applyButton, ap.appliedButton)}
                  >
                    <CheckIcon width={12} height={12} aria-hidden="true" />
                    Applied
                  </button>
                ) : (
                  /* A real <button>, not the anchor: the press no longer leaves the
               page. It hands off to the flow, which runs the sign-in gate, the
               profile check and the cover letter in place. */
                  <Button
                    size="s"
                    style="fill"
                    variant="primary"
                    className={ap.applyButton}
                    onClick={() => apply!.onApply({ role, teamId, teamName, team: team! })}
                  >
                    Apply
                  </Button>
                )))}
        </div>
      </div>

      <ReferModal
        open={referOpen}
        onClose={toggleReferOpen}
        role={role}
        teamId={teamId}
        teamName={teamName}
        source={source}
        jobReferEmail={team?.jobReferEmail}
      />
    </div>
  );
}
